<template>
  <div id="reserve-form-wizard" :class="$style.wizard" :style="accentVars">
    <!-- 進捗表示。番号・総数は filter 後の steps から導出する（STEP_DEFS の WHY コメント参照） -->
    <ol :class="$style.progress">
      <li
        v-for="(step, i) in steps"
        :key="step.id"
        :class="[
          $style.progressItem,
          i === currentStepIndex ? $style.current : null,
          i < currentStepIndex ? $style.done : null,
        ]"
      >
        <!-- 完了済み（done）だけクリックで戻れる。current・未到達は非インタラクティブな span のまま
             （前方ジャンプは満席・必須ゲートの漏斗を回避するため不可） -->
        <button
          v-if="i < currentStepIndex"
          type="button"
          :class="$style.progressBtn"
          :aria-label="`ステップ${i + 1} ${step.label} に戻る`"
          @click="goToStep(i)"
        >
          <span :class="$style.progressNum">{{ i + 1 }}</span>
          <span :class="$style.progressLabel">{{ step.label }}</span>
        </button>
        <template v-else>
          <span :class="$style.progressNum">{{ i + 1 }}</span>
          <span :class="$style.progressLabel">{{ step.label }}</span>
        </template>
      </li>
    </ol>
    <spacer :y="4" />

    <basic-box type="inner">
      <!-- ステップ本体を1枚の keyed wrapper で包み out-in で入替える。CSS Modules のため既定クラスは
           解決されず、enter/leave クラスを $style へ明示バインドする。
           mode="out-in" は :css=false だと enter 完了シグナルを得られず本体が空描画で stall するため、
           css は常に true に保つ。
           viewport の overflow:hidden で横スライド中のスクロールバー流出を抑える。 -->
      <div :class="$style.stepViewport">
      <transition
        mode="out-in"
        :enter-active-class="$style.stepActive"
        :leave-active-class="$style.stepActive"
        :enter-from-class="stepEnterFromClass"
        :leave-to-class="stepLeaveToClass"
      >
      <div :key="currentStepId">
      <!-- STEP: お悩みのテーマ（大項目と1:1の6カード・複数選択） -->
      <template v-if="currentStepId === 'theme'">
        <div :class="$style.stepHead">
          <p :class="$style.stepTitle">お悩みのテーマを選んでください</p>
          <p :class="$style.required">※必須項目</p>
        </div>
        <p :class="$style.stepNote">当てはまるものをいくつでも選べます。</p>
        <spacer :y="3" />
        <ul :class="$style.cardList">
          <li v-for="card in themeCards" :key="card.key">
            <button
              type="button"
              :class="[$style.card, isCardSelected(card.key) ? $style.selected : null]"
              :aria-pressed="isCardSelected(card.key)"
              :disabled="disabled"
              @click="toggleCard(card.key)"
            >
              <span :class="$style.cardLabel">{{ labelOfCard(card) }}</span>
              <span :class="$style.cardDesc">{{ card.desc }}</span>
            </button>
          </li>
        </ul>
      </template>

      <!-- STEP: お悩みの詳細（選んだテーマ配下のサブのみをチップ表示＝漏斗） -->
      <template v-else-if="currentStepId === 'sub'">
        <div :class="$style.stepHead">
          <p :class="$style.stepTitle">近いものを選んでください</p>
          <p :class="$style.required">※必須項目</p>
        </div>
        <p :class="$style.stepNote">選んだテーマごとに1つ以上お選びください。</p>
        <spacer :y="3" />

        <div v-for="card in selectedCardsWithSub" :key="card.key" :class="$style.subBlock">
          <p :class="$style.subBlockTitle">{{ labelOfCard(card) }}</p>
          <div
            v-for="(group, gi) in subGroupsOf(card.key)"
            :key="`${card.key}-${gi}`"
            :class="$style.subGroup"
          >
            <p v-if="group.label" :class="$style.subGroupLabel">{{ group.label }}</p>
            <ul :class="$style.chipList">
              <li v-for="entry in group.entries" :key="entry.subNum">
                <button
                  type="button"
                  :class="[$style.chip, isSubSelected(card.type, entry.subNum) ? $style.selected : null]"
                  :aria-pressed="isSubSelected(card.type, entry.subNum)"
                  :disabled="disabled"
                  @click="toggleSub(card.type, entry.subNum)"
                >{{ entry.label }}</button>
              </li>
            </ul>
          </div>
        </div>

        <!-- その他は master にサブを持たないため自由記述で受ける -->
        <div v-if="isOtherSelected" :class="$style.subBlock">
          <p :class="$style.subBlockTitle">{{ otherCardLabel }}</p>
          <label for="wizard-other-text" :class="$style.otherLabel">内容を入力してください</label>
          <input
            id="wizard-other-text"
            v-model="other_text"
            :class="$style.inputText"
            type="text"
            :disabled="disabled"
          >
        </div>
      </template>

      <!-- STEP: ココモニチェック（1問ずつ） -->
      <template v-else-if="currentStepId === 'cocomoni'">
        <div :class="$style.stepHead">
          <p :class="$style.stepTitle">ココモニ</p>
          <p :class="$style.required">※必須項目</p>
          <!-- バッジは margin-left:auto で右端へ寄せる（stepHead は flex・折返し時は見出し下へ回る） -->
          <span :class="$style.badge">ココモニチェック（約30秒・5問）</span>
        </div>
        <p :class="$style.stepNote">ご自身の今の状態を知ることで、より良いサポートにつながります。</p>
        <div :class="$style.divider"></div>
        <p :class="$style.stepNote">最近2週間の状態について、いちばん近いものをお選びください。</p>

        <!-- 進捗（現在問 / 総数・細バー）と設問ドット。master 未ロード時は総数が 0 になるので節ごと伏せる -->
        <div v-if="who5Total" :class="$style.who5Head">
          <div :class="$style.who5Count">
            <p><span :class="$style.who5CountNum">{{ currentWho5Index + 1 }}</span> / {{ who5Total }}</p>
            <div :class="$style.who5Bar"><span :style="{ width: `${who5Progress}%` }"></span></div>
          </div>
          <!-- 到達済み（先頭未回答まで）のみ活性。未到達への前方ジャンプはステップバーと同じく不可 -->
          <ul :class="$style.who5Dots">
            <li v-for="(item, i) in who5Items" :key="`who5-dot-${i}`">
              <button
                type="button"
                :class="[
                  $style.who5Dot,
                  who5Answers[i] !== null ? $style.done : null,
                  i === currentWho5Index ? $style.current : null,
                ]"
                :disabled="disabled || i > reachableWho5Index"
                :aria-label="`設問${i + 1}へ`"
                :aria-current="i === currentWho5Index ? 'true' : null"
                @click="goToWho5(i)"
              >Q{{ i + 1 }}</button>
            </li>
          </ul>
        </div>
        <spacer :y="3" />

        <!-- 設問は常に1問だけ描画し、現在問（who5Index）の入替えを out-in で見せる。
             CSS Modules では <transition> の既定クラスが解決されないため enter/leave を $style へ
             明示バインドする。mode="out-in" に :css=false 系を渡すと enter 完了シグナルを失って
             空描画で stall するため css は常に true に保つ。 -->
        <transition
          v-if="helper.master && helper.master.who5 && currentWho5Item"
          mode="out-in"
          :enter-active-class="$style.who5Active"
          :leave-active-class="$style.who5Active"
          :enter-from-class="$style.who5EnterFrom"
          :leave-to-class="$style.who5LeaveTo"
        >
          <!-- tag="div"（既定は li）。設問カードは ul/ol の外に置かれ、単一カードなので
               stack-item の `& + &` margin も効かない。li のままだとリスト外の li になる -->
          <stack-item
            :key="currentWho5Index"
            tag="div"
            :class="$style.who5item"
          >
            <basic-box type="inner">
              <div :class="$style.who5itemTitle"><span>Q{{ currentWho5Index + 1 }}.</span> {{ currentWho5Item.label }}</div>
              <spacer :y="1" />
              <ul :class="$style.optionList">
                <li
                  v-for="(option, j) in helper.master.who5.options"
                  :key="`${currentWho5Index}-${j}`"
                  :class="$style.optionItem"
                >
                  <!-- :checked はドット・戻る操作での復元表示に必須（旧フォームの radio は change のみ）。
                       click は「選択済みの選択肢を押し直した」ケース専用（radio は checked が
                       変わらないと change を出さないため、これが無いと押しても進まない）。 -->
                  <input
                    :id="`wizard-who5-${currentWho5Index}-${j}`"
                    type="radio"
                    :name="`wizard-who${currentWho5Index}`"
                    :checked="who5Answers[currentWho5Index] === option.value"
                    :disabled="disabled"
                    @change="updateRadioValue(currentWho5Index, option.value)"
                    @click="onWho5OptionClick(currentWho5Index, option.value)"
                  >
                  <label :for="`wizard-who5-${currentWho5Index}-${j}`">{{ option.label }}</label>
                </li>
              </ul>
            </basic-box>
          </stack-item>
        </transition>
      </template>

      <!-- STEP: 相談内容（テキストは必須／オンライン・バーチャルは任意の事前情報） -->
      <template v-else-if="currentStepId === 'message'">
        <div :class="$style.stepHead">
          <p :class="$style.stepTitle">{{ messageTitle }}</p>
          <p v-if="isText" :class="$style.required">※必須項目</p>
        </div>
        <p :class="$style.annotation">※絵文字や機種依存文字は文字化けの原因になります。<br> ご利用を避け、テキストのみご入力ください。</p>
        <spacer :y="1" />
        <textarea
          id="wizard-message"
          v-model="message"
          :class="$style.textarea"
          name="wizard-message"
          rows="12"
          :disabled="disabled"
          :placeholder="messagePlaceholder"
        ></textarea>
        <div :class="$style.count">
          <p :class="{ [$style.red]: over600 }">600字以内で入力してください。</p>
          <p>{{ textCount }}/<span :class="$style.bold">600</span></p>
        </div>
      </template>

      <!-- STEP: 確認（先生・希望日時・VCID は種別ゲートで行表示） -->
      <template v-else-if="currentStepId === 'confirm'">
        <div :class="$style.stepHead">
          <p :class="$style.stepTitle">この内容で送信します</p>
        </div>
        <spacer :y="3" />
        <data-grid>
          <data-row v-if="isOnlineOrVirtual && schedule.data">
            <data-term width="quater">
              <label-box :class="$style.label" label="先生" :color="labelBoxColor" />
            </data-term>
            <data-detail>
              <div :class="$style.flex">
                <round-img width="50px" :src="getMyIconUrl(schedule.data.counselor)" />
                <spacer :x="1.5" />
                <p><span :class="$style.bold">{{ schedule.data.counselor.username }}</span>先生（{{ helper.master.labels.users.account_type[schedule.data.counselor.account_type] }}）に相談</p>
              </div>
            </data-detail>
          </data-row>

          <data-row v-if="isVirtual && schedule.data">
            <data-term width="quater">
              <label-box :class="$style.label" label="Virtual Cast ID" :color="labelBoxColor" />
              <p v-if="!hasClientVirtualCastId" :class="$style.required">※必須項目</p>
            </data-term>
            <data-detail>
              <template v-if="hasClientVirtualCastId">
                <p>{{ user.virtualCastProp.virtual_cast_id }}</p>
                <p :class="$style.annotation">
                  以下のリンクがご自分のアカウントか確認してください。<br>
                  <a :href="getVirtualCastUserURL(user.virtualCastProp.virtual_cast_id)" target="_blank">{{ getVirtualCastUserURL(user.virtualCastProp.virtual_cast_id) }}</a>
                </p>
                <p :class="$style.annotation">万が一間違えて登録してしまった場合は営業日3日前までに事務局へ連絡ください。</p>
              </template>
              <template v-else>
                <div :class="[$style.vcid, $style.force_link]" @click="clickVcidInputButton">
                  <input-codes
                    :key="`wizard-vcid-${virtual_cast_id}`"
                    :digit="6"
                    :value="virtual_cast_id"
                    :smallMode="true"
                    :readonly="true"
                  />
                </div>
                <p v-if="virtual_cast_id" :class="$style.annotation">
                  <a :href="getVirtualCastUserURL(virtual_cast_id)" target="_blank">{{ getVirtualCastUserURL(virtual_cast_id) }}</a>
                </p>
              </template>
            </data-detail>
          </data-row>

          <data-row v-if="isOnlineOrVirtual && schedule.data">
            <data-term width="quater">
              <label-box :class="$style.label" label="希望日時" :color="labelBoxColor" />
            </data-term>
            <data-detail>
              <div :class="`${$style.flex} ${$style.space}`">
                <p>{{ filterMoment(schedule.data.date, 'Y年MM月DD日') }}</p>
                <spacer :x="2" />
                <p>{{ filterMoment(schedule.data.date, 'HH:mm') }} ~ {{ slotEndTime(schedule.data.date) }}</p>
              </div>
              <jst-time-note :date="schedule.data.date" />
            </data-detail>
          </data-row>

          <data-row>
            <data-term width="quater">
              <label-box :class="$style.label" label="お悩み" :color="labelBoxColor" />
            </data-term>
            <data-detail>
              <div v-for="row in worrySummary" :key="row.key" :class="$style.summaryRow">
                <p :class="$style.bold">{{ row.label }}</p>
                <p v-if="row.subs.length" :class="$style.summarySubs">{{ row.subs.join(' / ') }}</p>
                <p v-if="row.key === 'other' && other_text" :class="$style.summarySubs" class="multilineText">（{{ other_text }}）</p>
              </div>
            </data-detail>
          </data-row>

          <!-- 入力順（ココモニチェック → 相談内容）に合わせて確認欄も同順で並べる -->
          <data-row v-if="helper.master && helper.master.who5">
            <data-term width="quater">
              <label-box :class="$style.label" label="ココモニチェック" :color="labelBoxColor" />
            </data-term>
            <data-detail>
              <div
                v-for="(item, i) in helper.master.who5.items"
                :key="i"
                :class="$style.summaryRow"
              >
                <p :class="$style.summaryQ">Q{{ i + 1 }}. {{ item.label }}</p>
                <p :class="$style.bold">{{ who5OptionLabel(i) }}</p>
              </div>
            </data-detail>
          </data-row>

          <data-row v-if="message">
            <data-term width="quater">
              <label-box :class="$style.label" :label="messageTitle" :color="labelBoxColor" />
            </data-term>
            <data-detail>
              <!-- text を直に持つ要素への .n2br は no-op（.n2br > * は子要素のみ）。改行保持＋長語折返しは global .multilineText で担保 -->
              <p class="multilineText">{{ message }}</p>
            </data-detail>
          </data-row>
        </data-grid>
      </template>
      </div>
      </transition>
      </div>
    </basic-box>

    <spacer :y="4" />
    <btn-container>
      <basic-btn v-if="currentStepIndex > 0" tag="button" @click="goBack">戻る</basic-btn>
      <spacer v-if="currentStepIndex > 0" :x="2" />
      <basic-btn
        v-if="currentStepId !== 'confirm'"
        tag="button"
        :disabled="disabled || isAdvancing"
        @click="goNext"
      >次へ</basic-btn>
      <basic-btn
        v-else
        tag="button"
        :disabled="disabled || isSended"
        @click="onSubmit"
      >送信する</basic-btn>
    </btn-container>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import BasicBtn from '@/views/components/BasicBtn.vue';
import BtnContainer from '@/views/components/BtnContainer.vue';
import BasicBox from '@/views/components/BasicBox.vue';
import Spacer from '@/views/components/Spacer.vue';
import StackItem from '@/views/components/StackItem.vue';
import RoundImg from '@/views/components/RoundImg';
import LabelBox from '@/views/components/LabelBox';
import JstTimeNote from '@/views/components/JstTimeNote.vue';
import InputCodes from '@/views/components/parts/Form/InputCodes.vue';
import {
  DataGrid, DataRow, DataTerm, DataDetail,
} from '@/views/components/DataGrid';
import cf from '@/mixins/commonFunctions';
import corporateSchool from '@/mixins/corporateSchool';
import {
  THEME_CARDS,
  OTHER_TYPE,
  cardByKey,
  cardLabel,
  cardsToSelectItems,
  subEntriesForCard,
  subGroupsForCard,
  missingSubCards,
} from '@/lib/worryThemes';

// ステップ定義。番号・総数・進捗はすべて filter 後の配列 index から導出するため、
// テンプレート/ロジックにステップ番号を直書きしないこと。学校への全展開時は sub の when を
// 落とすだけで 3 ステップへ縮み、番号の振り直しは不要になる。
const STEP_DEFS = [
  { id: 'theme', label: 'お悩み', when: () => true },
  { id: 'sub', label: '詳細', when: (ctx) => ctx.isCorporate },
  { id: 'cocomoni', label: 'ココモニ', when: () => true },
  // ラベルは種別不問で「相談内容」に固定する。見出し（messageTitle）も同語で統一済みなので、
  // ステップバー・見出し・確認欄・字数超過エラーがすべて同じ語になる。
  { id: 'message', label: '相談内容', when: () => true },
  { id: 'confirm', label: '確認', when: () => true },
];

// お悩みラベルの参照ブロック。語彙は master が SoT で、予約詳細・履歴側も同じ日常語を引く。
// reserve / texts は同値だが、参照ブロックは旧フォームと同じ texts に揃えてある。
const LABEL_BLOCK = 'texts';

export default {
  name: 'reserve-form-wizard',
  mixins: [cf, corporateSchool],
  components: {
    BasicBtn,
    BtnContainer,
    BasicBox,
    Spacer,
    StackItem,
    RoundImg,
    LabelBox,
    InputCodes,
    JstTimeNote,
    DataGrid,
    DataRow,
    DataTerm,
    DataDetail,
  },
  props: {
    // 'text' | 'online' | 'virtual'
    pagetype: {
      type: String,
      required: true,
    },
    schedule: {
      type: Object,
      default: () => ({ id: null, data: null }),
    },
    // テキスト相談の利用不可状態（入力を触らせない）
    disabled: {
      type: Boolean,
      default: false,
    },
    // 以下は親（ReserveForm）が store から導出済みの送信制約。ウィザードに store の
    // データ形状を持ち込まないため props で受ける（ステップのゲート軸である isCorporate のみ
    // 入力 UI の表示ゲートと同軸である必要があるので、ウィザード側で computed する）。
    hasProgress: {
      type: Boolean,
      default: false,
    },
    hasClientVirtualCastId: {
      type: Boolean,
      default: false,
    },
    isSended: {
      type: Boolean,
      default: false,
    },
    // 親（ReserveForm）の予約可否再チェック。予約済み/満席なら親が alert＋枠一覧へ戻し false を返す。
    // schedule fetch と遷移の責務を親に集約するため関数 prop で受ける（store 形状を持ち込まない既存方針）。
    verifyReservable: {
      type: Function,
      default: () => Promise.resolve(true),
    },
  },
  emits: ['submit'],
  data() {
    return {
      currentStepIndex: 0,
      // 確認ステップ進入時の満席チェック待ちフラグ（「次へ」の二重発火ガード）
      isAdvancing: false,
      // ステップ遷移アニメの向き。goNext=forward / goBack・番号クリック=back。
      // enter-from / leave-to クラスをこの向きで出し分ける（初期値は前進）。
      transitionDir: 'forward',
      selectedCards: [],
      // メイン番号（1-5）→ 選択されたサブ番号配列。保存形と同じ形で持つ
      selectSubItems: {
        1: [],
        2: [],
        3: [],
        4: [],
        5: [],
      },
      other_text: null,
      message: null,
      who5Answers: [null, null, null, null, null],
      // ココモニの現在問。回答状態から導出せず data に持つ（導出値だとドットで戻っても
      // 回答状態から再計算されて戻り先が上書きされ、操作が効かない）。前進は
      // advanceFromWho5 に一本化する。
      who5Index: 0,
      virtual_cast_id: null,
    };
  },
  computed: {
    ...mapState(['helper', 'user']),
    themeCards() {
      return THEME_CARDS;
    },
    // 入力 UI の表示ゲート。サブ必須チェックの発火条件と同軸でなければ
    // 「UI が出ないのに必須チェックだけ走る」false-block になる（旧 checkConfirm の isCompany 軸がこれ）
    isCorporate() {
      return this.isCorporateSchool(this.user?.school);
    },
    isText() {
      return this.pagetype === 'text';
    },
    isOnline() {
      return this.pagetype === 'online';
    },
    isVirtual() {
      return this.pagetype === 'virtual';
    },
    isOnlineOrVirtual() {
      return this.isOnline || this.isVirtual;
    },
    stepCtx() {
      return {
        isCorporate: this.isCorporate,
        isText: this.isText,
        isOnline: this.isOnline,
        isVirtual: this.isVirtual,
      };
    },
    // 不変条件: サブ必須チェックの発火軸は、サブ入力 UI の表示ゲートと同軸でなければならない。
    // ステップが filter で落ちる構成なので「sub ステップが在るか」を軸に使う（in-UI ゲートそのもの）。
    // 恒真の isCorporate を直接見ると、学校展開で sub を落とした瞬間に
    // 「サブ UI が出ないのに必須チェックだけ走る」false-block を再生する（#3242 の軸ズレの再来）。
    hasSubStep() {
      return this.steps.some((step) => step.id === 'sub');
    },
    steps() {
      return STEP_DEFS.filter((def) => def.when(this.stepCtx));
    },
    currentStepId() {
      return this.steps[this.currentStepIndex]?.id;
    },
    labelBoxColor() {
      return this.isVirtual ? 'green' : null; // null = default の orange が採用される
    },
    // 選択ハイライトのアクセント色を pagetype 規定色に合わせる（virtual=緑 / online・text=橙）。
    // 確認ステップの label-box（labelBoxColor）や他の予約系サーフェスと同じ規定に揃える。
    // card/chip の .selected と hover が var(--wz-accent) 経由でこの値を引く。
    accentVars() {
      return this.isVirtual
        ? { '--wz-accent': 'var(--green-main)', '--wz-accent-rgb': 'var(--green-main-rgb)' }
        : { '--wz-accent': 'var(--orange-main)', '--wz-accent-rgb': 'var(--orange-main-rgb)' };
    },
    // 見出しは相談種別によらず「相談内容」で統一する（旧 1 画面フォームの label-box も同語）。
    // computed のまま残すのは、確認画面の label-box と 600 字超過 alert が同じ語を参照しており、
    // 文言の出所を 1 箇所に保つため。
    messageTitle() {
      return '相談内容';
    },
    messagePlaceholder() {
      // 見出しと同じ「相談内容」を使いつつ、オンライン/バーチャルでは入力任意なので
      // 「〜があればご記入ください」の言い回しを保つ（「入力してください」だと必須に見える）。
      return this.isOnlineOrVirtual ? '担当の医師・心理士に伝えておきたい相談内容があればご記入ください。' : '';
    },
    textCount() {
      return this.message ? this.message.length : 0;
    },
    over600() {
      return this.textCount > 600;
    },
    // master 順（＝type 昇順）で返す。選択順に依存させない
    selectedCardsOrdered() {
      return THEME_CARDS.filter((card) => this.selectedCards.includes(card.key));
    },
    // サブを持つカードのみ（その他は master にサブが無いので自由記述ブロックへ回す）
    selectedCardsWithSub() {
      return this.selectedCardsOrdered.filter((card) => this.subEntriesOf(card.key).length);
    },
    // master 由来の「サブが無いカード」で引くと、master ロード前は全カードのサブが空になり
    // 先頭カードを誤ってその他と判定するため、モジュールの定数で決定的に引く
    otherCard() {
      return THEME_CARDS.find((card) => card.type === OTHER_TYPE);
    },
    isOtherSelected() {
      return !!this.otherCard && this.selectedCards.includes(this.otherCard.key);
    },
    otherCardLabel() {
      return this.labelOfCard(this.otherCard);
    },
    labels() {
      return this.helper?.master?.labels;
    },
    missingSubCardKeys() {
      return missingSubCards(this.selectedCards, this.selectSubItems, this.labels, LABEL_BLOCK);
    },
    who5Items() {
      return this.helper?.master?.who5?.items ?? [];
    },
    who5Total() {
      return this.who5Items.length;
    },
    // master ロード前・件数変動でも範囲外の設問を指さないようクランプする（描画は
    // currentWho5Item の null ガード下なので、0 件のときは設問カードごと出ない）
    currentWho5Index() {
      if (!this.who5Total) return 0;
      return Math.min(this.who5Index, this.who5Total - 1);
    },
    currentWho5Item() {
      return this.who5Items[this.currentWho5Index] ?? null;
    },
    who5Progress() {
      if (!this.who5Total) return 0;
      return Math.round(((this.currentWho5Index + 1) / this.who5Total) * 100);
    },
    // ドットで戻れる上限＝先頭未回答（全問回答済みなら最終問）。未回答より先への
    // 前方ジャンプは、ステップバーの「完了済みのみ後退」と同じ思想で許可しない。
    reachableWho5Index() {
      const last = Math.max(this.who5Total - 1, 0);
      const firstUnanswered = this.who5Answers.findIndex((answer) => answer === null);
      return firstUnanswered === -1 ? last : Math.min(firstUnanswered, last);
    },
    allWho5Answered() {
      return !this.who5Answers.includes(null);
    },
    worrySummary() {
      return this.selectedCardsOrdered.map((card) => {
        const selected = (this.selectSubItems[card.type] ?? []).map(Number);
        return {
          key: card.key,
          label: this.labelOfCard(card),
          subs: this.subEntriesOf(card.key)
            .filter((entry) => selected.includes(entry.subNum))
            .map((entry) => entry.label),
        };
      });
    },
    // ステップ遷移トランジションの enter-from / leave-to クラスを向きで出し分ける。
    // Vue <transition> の既定クラスは CSS Modules に解決されないため $style を明示バインドする。
    // active クラスは共通（transition プロパティのみ）。forward=新は右から入り旧は左へ抜ける。
    stepEnterFromClass() {
      return this.transitionDir === 'back' ? this.$style.enterFromLeft : this.$style.enterFromRight;
    },
    stepLeaveToClass() {
      return this.transitionDir === 'back' ? this.$style.leaveToRight : this.$style.leaveToLeft;
    },
  },
  watch: {
    // ココモニ節へ入り直したときの起点をそろえる。未回答が残っていれば続きから（続行）、
    // 全問回答済みでの再入場は見直し目的なので先頭 Q1 から見せる。離脱位置をそのまま復帰
    // させると「Q5 が出て何を見直すのか分からない」状態になる。
    currentStepId(id) {
      if (id !== 'cocomoni') return;
      const firstUnanswered = this.who5Answers.findIndex((answer) => answer === null);
      this.who5Index = firstUnanswered === -1 ? 0 : firstUnanswered;
    },
  },
  methods: {
    labelOfCard(card) {
      return cardLabel(card, this.labels, LABEL_BLOCK);
    },
    subEntriesOf(cardKey) {
      return subEntriesForCard(cardKey, this.labels, LABEL_BLOCK);
    },
    subGroupsOf(cardKey) {
      return subGroupsForCard(cardKey, this.labels, LABEL_BLOCK);
    },
    isCardSelected(cardKey) {
      return this.selectedCards.includes(cardKey);
    },
    toggleCard(cardKey) {
      const card = cardByKey(cardKey);
      if (!card) return;
      if (this.isCardSelected(cardKey)) {
        this.selectedCards = this.selectedCards.filter((key) => key !== cardKey);
        // カードを外したら配下のサブもクリア（旧フォームの updateCheckValue と同じ挙動）
        if (this.selectSubItems[card.type]) {
          this.selectSubItems = { ...this.selectSubItems, [card.type]: [] };
        }
        if (this.otherCard && cardKey === this.otherCard.key) this.other_text = null;
      } else {
        this.selectedCards = [...this.selectedCards, cardKey];
      }
    },
    isSubSelected(typeNum, subNum) {
      return (this.selectSubItems[typeNum] ?? []).map(Number).includes(subNum);
    },
    toggleSub(typeNum, subNum) {
      const list = (this.selectSubItems[typeNum] ?? []).slice();
      const idx = list.indexOf(subNum);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(subNum);
      this.selectSubItems = { ...this.selectSubItems, [typeNum]: list };
    },
    updateRadioValue(i, value) {
      this.who5Answers[i] = value;
      this.advanceFromWho5(i);
    },
    // 選択済みの選択肢を押し直したケース専用。押した値が既にモデルの値と同じ＝この操作では
    // change が出ないので、ここで前進を担う。別の選択肢を押した場合はモデルがまだ旧値なので
    // 何もせず、続いて走る change 側（updateRadioValue）が前進する。
    onWho5OptionClick(i, value) {
      if (this.who5Answers[i] !== value) return;
      this.advanceFromWho5(i);
    },
    // 「選んだら次へ進む」を回答済み・未回答を問わず統一する前進処理。最終問では次ステップ
    // （相談内容）へ送り、初回通過で「次へ」を押す手数を無くす。経路は goNext に一本化する
    // ので必須チェック・予約可否チェックは「次へ」と同じまま。
    // ガード2つは多重前進の防止: click と change が同一操作で続けて走っても、1回目の前進で
    // 現在問（またはステップ）が移るため2回目は素通りする。
    advanceFromWho5(i) {
      if (this.currentStepId !== 'cocomoni') return;
      if (i !== this.currentWho5Index) return;
      if (i < this.who5Total - 1) {
        this.who5Index = i + 1;
        return;
      }
      if (this.allWho5Answered) this.goNext();
    },
    // ドットからの設問移動。到達済み（先頭未回答まで）のみ許可する。
    goToWho5(i) {
      if (i > this.reachableWho5Index) return;
      this.who5Index = i;
    },
    who5OptionLabel(i) {
      const option = (this.helper?.master?.who5?.options ?? []).find((row) => row.value === this.who5Answers[i]);
      return option ? option.label : '';
    },
    clickVcidInputButton() {
      const args = {
        modalName: 'input-vcid',
        data: {
          callback: (vcid) => {
            this.virtual_cast_id = vcid;
          },
        },
      };
      this.$store.dispatch('modal/contents/showModal', args, { root: true });
    },
    /** 現在ステップの「次へ」を許可するか（旧 checkConfirm の項目別分解） */
    validateCurrentStep() {
      if (this.currentStepId === 'theme') {
        if (!this.selectedCards.length) {
          alert('お悩みの種別を選択してください');
          return false;
        }
      }
      if (this.currentStepId === 'sub') {
        if (this.isOtherSelected && !this.other_text) {
          alert('お悩みのその他の内容を入力してください');
          return false;
        }
        if (this.missingSubCardKeys.length) {
          alert('お悩みの詳細項目を1つ以上選択してください');
          return false;
        }
      }
      if (this.currentStepId === 'cocomoni') {
        if (!this.allWho5Answered) {
          alert('ココモニチェックはすべて回答してください');
          return false;
        }
      }
      if (this.currentStepId === 'message') {
        // 必須は text のみ（表示の ※必須項目 と同じ isText を軸にする＝軸ズレによる false-block を作らない）
        if (this.isText && !this.message) {
          alert('相談内容を入力してください');
          return false;
        }
        // 文言は見出し（messageTitle）に追従させる。見出しを変えたときに alert だけ旧語が残る
        // 乖離を防ぐため、固定文言では書かない。
        if (this.over600) {
          alert(`${this.messageTitle}は600字以内で入力してください。`);
          return false;
        }
      }
      return true;
    },
    /**
     * 確認ステップ進入時のガード（送信直前の事前拒否＝旧 checkConfirm 冒頭と同位置）。
     * hasProgress / オンライン満席の2点に加え、サブ必須・WHO-5 全問・相談内容を最終ガードとして
     * 再判定する（各ステップの判定を通っていれば通常は素通りする防御的チェック）。
     */
    async canEnterConfirm() {
      if (this.isText && this.hasProgress) {
        alert('相談中のやりとりが残っているため、新たに相談内容を登録することはできません。');
        return false;
      }
      // 予約済み・online 満席の再確認は親の verifyReservable に一本化（NG は親が alert＋枠一覧へ）。
      // 確認ステップ進入は最終ゲートなので同期（await）で通す。
      if (!(await this.verifyReservable())) return false;
      if (this.hasSubStep && this.missingSubCardKeys.length) {
        alert('お悩みの詳細項目を1つ以上選択してください');
        return false;
      }
      if (!this.allWho5Answered) {
        alert('ココモニチェックはすべて回答してください');
        return false;
      }
      if (this.isText && !this.message) {
        alert('相談内容を入力してください');
        return false;
      }
      if (this.over600) {
        alert(`${this.messageTitle}は600字以内で入力してください。`);
        return false;
      }
      return true;
    },
    async goNext() {
      // 確認ステップ進入時の満席チェックはネットワーク往復を待つ。その間「次へ」を押せると
      // 両クリックが同じ currentStepIndex を読んで二重に加算し、ステップ範囲外（どの
      // template にも当たらない空白表示）へ進む。in-flight 中は先着のみ通す。
      if (this.isAdvancing) return;
      if (!this.validateCurrentStep()) return;
      const next = this.steps[this.currentStepIndex + 1];
      if (!next) return;
      this.isAdvancing = true;
      try {
        if (next.id === 'confirm') {
          // 確認ステップ進入は同期（ブロッキング）で最終ゲートを通す。
          if (!(await this.canEnterConfirm())) return;
        } else {
          // フォーム内のステップ移動は予約可否を非同期チェック（await せず＝進行を妨げない）。
          // 予約済み/満席なら親の verifyReservable が背景で alert＋枠一覧へ戻して事後是正する。
          this.verifyReservable();
        }
        this.transitionDir = 'forward';
        this.currentStepIndex += 1;
        this.scrollToWizardTop();
      } finally {
        this.isAdvancing = false;
      }
    },
    goBack() {
      // goNext の満席チェック await 中に「戻る」を押させない（-=1 後に await 解決の +=1 が
      // 誤 index 加算する、番号クリック goToStep と同族のレースを塞ぐ）。
      if (this.isAdvancing) return;
      if (this.currentStepIndex === 0) return;
      this.transitionDir = 'back';
      this.currentStepIndex -= 1;
      this.scrollToWizardTop();
    },
    // 上部ステップ番号のクリック。完了済み（done＝i < currentStepIndex）へ"戻る"のみ許可。
    // 前方ジャンプは満席チェック・必須ゲートの漏斗を回避するため不可。
    goToStep(i) {
      if (this.isAdvancing) return; // goNext の await 中は弾く（着眼点1）
      if (i >= this.currentStepIndex) return; // 現在・未到達は無視（done のみ後退）
      this.transitionDir = 'back';
      this.currentStepIndex = i;
      this.scrollToWizardTop();
    },
    scrollToWizardTop() {
      this.$nextTick(() => {
        const elem = document.getElementById('reserve-form-wizard');
        if (!elem) return;
        const position = elem.getBoundingClientRect().top;
        // scroll-into-view-if-needed: wizard 上部が既にヘッダー下(77px)以降で見えているなら動かさない。
        // 前ステップで下までスクロールして上部がヘッダーに隠れている(position < 77)時だけ上部へ戻す。
        // 本画面は横スライド遷移を持つため、毎回 smooth スクロール(html の scroll-behavior:smooth を継承)
        // すると縦スクロール＋横スライドの二重モーションになり煩雑。冗長な整列を抑止する。
        if (position >= 77) return;
        scrollBy(0, position - 77);
      });
    },
    // 送信境界の防御フィルタ: 選択中の大項目(type)のサブだけを残し、非選択 type は強制的に空にする。
    // 平時は toggleCard の deselect クリア（配下サブの空化）で整合するが、その単一点が回帰・別経路で
    // selectedCards が変わった場合でも「selectItems と selectSubItems の不整合＝非選択カードのサブ混入」を
    // submit で塞ぐ（robust-by-construction）。保存契約の {1..5} 形状は維持する（旧フォームと同じ）。
    prunedSubItemsForSubmit() {
      const selectedTypes = cardsToSelectItems(this.selectedCards);
      const result = {};
      Object.keys(this.selectSubItems).forEach((typeNum) => {
        result[typeNum] = selectedTypes.includes(Number(typeNum))
          ? (this.selectSubItems[typeNum] ?? [])
          : [];
      });
      return result;
    },
    onSubmit() {
      // VCID は確認ステップの送信クリック時に判定する（旧フォームと同型の UX）
      if (this.isVirtual && !this.hasClientVirtualCastId && !this.virtual_cast_id) {
        alert('Virtual Cast ID を入力してください。');
        return;
      }
      // 保存経路は親が持つ。ウィザードは入力値一式を渡すだけ（保存契約の二重管理を避ける）。
      // selectSubItems は非選択カードのサブ混入を防ぐため prune してから渡す（上記メソッド参照）。
      this.$emit('submit', {
        selectItems: cardsToSelectItems(this.selectedCards),
        selectSubItems: this.prunedSubItemsForSubmit(),
        other_text: this.other_text,
        message: this.message,
        who5Answers: this.who5Answers,
        virtual_cast_id: this.virtual_cast_id,
      });
    },
  },
};
</script>

<style lang="scss" module>
// ステップ遷移・逐次表示アニメの共通トークン（このコンポーネント内で一貫させる）
$wizard-slide: 12px;
$wizard-duration: .24s;
$wizard-ease: ease-out;

.wizard {
  // 横スライド中の 12px はみ出しをここでクリップ（body 横スクロールバーの流出防止）。
  // 高さは auto なので縦内容は非クリップ（out-in で高さはステップ間でジャンプする＝許容）。
  .stepViewport {
    overflow: hidden;
  }
  // out-in の active（enter/leave 共通）。opacity と transform を同トーンで補間。
  .stepActive {
    transition: opacity $wizard-duration $wizard-ease, transform $wizard-duration $wizard-ease;
  }
  // 進む: 新は右から入る／旧は左へ抜ける。戻る: 新は左から入る／旧は右へ抜ける。
  .enterFromRight {
    opacity: 0;
    transform: translateX($wizard-slide);
  }
  .enterFromLeft {
    opacity: 0;
    transform: translateX(-$wizard-slide);
  }
  .leaveToLeft {
    opacity: 0;
    transform: translateX(-$wizard-slide);
  }
  .leaveToRight {
    opacity: 0;
    transform: translateX($wizard-slide);
  }
  // ココモニ節の逐次表示（WHO-5 の新出1問）。上方向へ軽く持ち上げつつフェードイン。
  .who5Active {
    transition: opacity $wizard-duration $wizard-ease, transform $wizard-duration $wizard-ease;
  }
  .who5EnterFrom {
    opacity: 0;
    transform: translateY(7px);
  }
  // 旧問は上へ抜ける（新問は下から入る）＝設問が1問ずつ送られる向きを揃える
  .who5LeaveTo {
    opacity: 0;
    transform: translateY(-7px);
  }
  // 視差過敏設定では transform/opacity 補間を無効化（外側ステップ遷移・内側逐次表示の両方）。
  // 視差過敏設定の無効化はこのブロックに集約する。ただし後段で定義するクラス（.who5Bar 等）は
  // ここに書くと同一詳細度で後勝ちに負けるため、各定義の直下に個別の media を置く。
  @media (prefers-reduced-motion: reduce) {
    .stepActive,
    .who5Active {
      transition: none;
    }
    .enterFromRight,
    .enterFromLeft,
    .leaveToLeft,
    .leaveToRight,
    .who5EnterFrom,
    .who5LeaveTo {
      opacity: 1;
      transform: none;
    }
  }

  .progress {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    list-style: none;
    padding: 0;
    margin: 0;

    .progressItem {
      display: flex;
      flex-direction: column;
      align-items: center;
      color: #888;
      font-size: 12px;
      min-width: 70px;

      @include sm-view {
        min-width: 56px;
      }

      // done のクリック可能番号。li の縦積みを button 内で再現しつつボタン既定装飾を除去。
      // 色は親 progressItem（.done）から継承させるため color/font は触らない。
      .progressBtn {
        display: flex;
        flex-direction: column;
        align-items: center;
        border: none;
        background: none;
        padding: 0;
        margin: 0;
        color: inherit;
        font: inherit;
        cursor: pointer;

        @media (hover: hover) {
          &:hover .progressNum {
            opacity: .85;
          }
        }
      }

      .progressNum {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background-color: var(--gray-main);
        color: #fff;
        font-weight: bold;
        margin-bottom: 4px;
      }

      &.current {
        color: var(--orange-main);
        font-weight: bold;
        .progressNum {
          background-color: var(--orange-main);
        }
      }

      &.done {
        color: var(--blue-main);
        .progressNum {
          background-color: var(--blue-main);
        }
      }
    }

    .progressItem + .progressItem {
      margin-left: 12px;

      @include sm-view {
        margin-left: 6px;
      }
    }
  }

  .stepHead {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .stepTitle {
    font-size: 20px;
    font-weight: bold;

    @include sm-view {
      font-size: 17px;
    }
  }
  .stepNote {
    font-size: 13px;
    color: #888;
    margin-top: 4px;
  }
  .required {
    font-size: 12px;
    color: red;
  }
  // 見出し右のバッジ（pill）。Bootstrap 非読込のため自前定義。色は pagetype 規定の
  // アクセント（virtual=緑 / online・text=橙）を薄塗り＋文字色で引く。
  .badge {
    margin-left: auto;
    padding: 4px 12px;
    border-radius: 2em;
    background-color: rgba(var(--wz-accent-rgb), .1);
    color: var(--wz-accent);
    font-size: 12px;
    font-weight: bold;
  }
  .divider {
    margin: 12px 0;
    border-top: 1px solid var(--gray-main);
  }
  .annotation {
    font-size: 12px;
  }
  .bold {
    font-weight: bold;
  }
  .flex {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  .space {
    margin-top: 8px;
    @include sm-view {
      margin-top: 0;
    }
  }
  .label {
    @include sm-view {
      width: 150px;
    }
  }

  // STEP1: テーマカード
  .cardList {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;

    @include sm-view {
      grid-template-columns: 1fr;
    }

    .card {
      width: 100%;
      height: 100%;
      text-align: left;
      padding: 14px 16px;
      border: 2px solid var(--gray-main);
      border-radius: 10px;
      background-color: #fff;
      cursor: pointer;
      transition: all .2s;

      &:disabled {
        cursor: default;
        opacity: .6;
      }

      @media (hover: hover) {
        &:hover:not(:disabled) {
          border-color: var(--wz-accent);
        }
      }

      &.selected {
        border-color: var(--wz-accent);
        background-color: rgba(var(--wz-accent-rgb), .1);
      }

      .cardLabel {
        display: block;
        font-size: 16px;
        font-weight: bold;
      }
      .cardDesc {
        display: block;
        margin-top: 4px;
        color: #888;
        font-size: 12px;
        line-height: 1.4;
      }
    }
  }

  // STEP2: サブチップ
  .subBlock + .subBlock {
    margin-top: 20px;
  }
  .subBlockTitle {
    font-weight: bold;
    font-size: 15px;
    padding-bottom: 4px;
    border-bottom: 1px solid var(--gray-main);
  }
  .subGroup {
    margin-top: 10px;
  }
  .subGroupLabel {
    font-size: 12px;
    color: #888;
    margin-bottom: 4px;
  }
  .chipList {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    .chip {
      padding: 6px 14px;
      border: 1px solid var(--gray-main);
      border-radius: 2em;
      background-color: #fff;
      font-size: 13px;
      cursor: pointer;
      transition: all .2s;

      &:disabled {
        cursor: default;
        opacity: .6;
      }

      @media (hover: hover) {
        &:hover:not(:disabled) {
          border-color: var(--wz-accent);
        }
      }

      &.selected {
        border-color: var(--wz-accent);
        background-color: var(--wz-accent);
        color: #fff;
      }
    }
  }
  .otherLabel {
    display: block;
    margin: 10px 0 4px;
    font-size: 13px;
  }
  // 単一行 input のサイト共通スタイル（グレー塗り・枠線なし・角丸8px・padding13px）。
  // 参照: Edit/Profile.vue の .text。本コンポーネントの .textarea とも padding を揃える。
  // autofill の黄背景がグレー塗りを上書きするのを inset box-shadow で抑止する（標準慣行）。
  .inputText {
    width: 100%;
    padding: 13px;
    border: none;
    outline: none;
    border-radius: 8px;
    background-color: var(--gray-sub);
    $background: var(--gray-sub);
    &:-webkit-autofill {
      box-shadow: 0 0 0 100px $background inset;
    }
  }

  // ココモニ節（進捗＋設問ドット）
  .who5Head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 12px;
  }
  .who5Count {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #888;

    .who5CountNum {
      font-size: 20px;
      font-weight: bold;
      color: var(--wz-accent);
    }
  }
  .who5Bar {
    width: 120px;
    height: 4px;
    border-radius: 2em;
    background-color: var(--gray-sub);
    overflow: hidden;

    span {
      display: block;
      height: 100%;
      background-color: var(--wz-accent);
      transition: width $wizard-duration $wizard-ease;
    }

    // 上部の集約ブロックではなくここに置く（あちらは本定義より前にあり後勝ちで負ける）
    @media (prefers-reduced-motion: reduce) {
      span {
        transition: none;
      }
    }
  }
  .who5Dots {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;

    // 未到達（先頭未回答より先）は disabled。回答済み=薄塗り / 現在問=塗りつぶしで区別する
    .who5Dot {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 1px solid var(--gray-main);
      background-color: #fff;
      color: #888;
      font-size: 11px;
      font-weight: bold;
      cursor: pointer;
      transition: all .2s;

      &:disabled {
        cursor: default;
        opacity: .5;
      }

      &.done {
        border-color: var(--wz-accent);
        background-color: rgba(var(--wz-accent-rgb), .1);
        color: var(--wz-accent);
      }

      &.current {
        border-color: var(--wz-accent);
        background-color: var(--wz-accent);
        color: #fff;
      }
    }
  }

  // ココモニ節（設問カード）
  .who5item {
    .who5itemTitle {
      font-weight: bold;
      span {
        margin-right: 4px;
      }
    }
    .optionList {
      list-style: none;
      padding: 0;
      margin: 0;
      .optionItem {
        display: flex;
        align-items: center;
        gap: 4px;
      }
    }
  }
  // サイト内 textarea の支配的慣行に合わせる（グレー塗り・枠線なし・角丸8px・縦リサイズのみ）。
  // 参照: ReserveDetailImpl / Settings / ReserveCancel / RiskApplicationForm（いずれも var(--gray-sub)）。
  // resize:vertical はブラウザ既定の両方向リサイズを抑えつつ Firefox でも縦伸縮を許す。
  .textarea {
    width: 100%;
    resize: vertical;
    border: none;
    outline: none;
    border-radius: 8px;
    padding: 13px;
    background-color: var(--gray-sub);
    // App.vue のグローバル ::placeholder は #bbb で、この欄はグレー塗り（--gray-sub）のため
    // コントラスト比が約 1.9:1 しかなく読みづらい。同画面の説明文（#888）より薄いので、
    // 4.5:1 を満たす濃さへ上書きする。グローバル側を触ると全画面に波及するのでここで局所化する。
    &::placeholder {
      color: #6d6d6d;
    }
  }
  .count {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    .red {
      color: red;
    }
  }

  // 確認ステップ
  .summaryRow + .summaryRow {
    margin-top: 10px;
  }
  .summarySubs {
    font-size: 13px;
    color: #555;
  }
  .summaryQ {
    font-size: 12px;
    color: #888;
  }
  .vcid {
    display: inline-block;
  }
  .force_link {
    cursor: pointer;
  }
}
</style>
